import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { CheckHourly } from '../../types';
import { useChartTheme } from '../../utils/chartTheme';
import { TimeWindow, buildUptimeSeries, timeTickFormatter } from '../../utils/series';

interface Props {
  hourlyStats: CheckHourly[];
  /** The selected range; the x-axis spans exactly this (see windowBounds). */
  timeWindow: TimeWindow;
}

export function UptimeChart({ hourlyStats, timeWindow }: Props) {
  const t = useChartTheme();
  const data = buildUptimeSeries(hourlyStats);
  const format = timeTickFormatter(timeWindow[1] - timeWindow[0]);

  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
        No hourly data yet — this fills in as checks accumulate.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={250}>
      <BarChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={t.grid} vertical={false} />
        <XAxis
          dataKey="t"
          type="number"
          scale="time"
          domain={timeWindow}
          allowDataOverflow
          tickFormatter={format}
          tick={{ fontSize: 11, fill: t.axis }}
          stroke={t.grid}
          minTickGap={40}
        />
        <YAxis tick={{ fontSize: 11, fill: t.axis }} stroke={t.grid} domain={[0, 100]} unit="%" width={48} />
        <Tooltip
          contentStyle={t.tooltip}
          labelStyle={{ color: t.axis }}
          cursor={{ fill: t.grid, fillOpacity: 0.35 }}
          labelFormatter={(v: number) => new Date(v).toLocaleString()}
          formatter={(value: number) => [`${value}%`, 'Uptime']}
        />
        <Bar dataKey="uptime" radius={[2, 2, 0, 0]} isAnimationActive={false}>
          {/* A degraded hour should not look the same as a clean one. */}
          {data.map(p => (
            <Cell
              key={p.t}
              fill={p.uptime >= 99 ? t.bar : p.uptime >= 90 ? t.degraded : t.down}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
